/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, createStyles, FormControl, FormControlLabel, FormGroup, FormLabel, IconButton, makeStyles, Theme, Tooltip, withStyles } from '@material-ui/core';
import React from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import CloseIcon from '@material-ui/icons/Close';
import { formatNumber } from '../../helpers/ChartHelper';
import { SelectedItemProps } from '../MattersByStatusChart';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import MTooltip from '@material-ui/core/Tooltip';
import { ZoomableGroup, ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { MatterByCountryParams, MatterCountry, useMatterByCountryQuery } from './hooks/useMatterByCountryQuery';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import PublicOutlinedIcon from '@material-ui/icons/PublicOutlined';
import { RV_CHART_CLASS, RV_CHART_COLORS } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { SearchCriteria } from '../SearchCriteria';
import { DrillDownType } from '../DrillDownType';
import { cdnUrl } from '../../App';
import { ChartInfo } from '../../components/ChartInfo';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        formGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            overflow: 'hidden',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            // '& svg': {
            //     position: 'absolute',
            //     top: 0,
            //     bottom: 0,
            //     left: 0,
            //     right: 0,
            // },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        legend: {
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            padding: '2px 15px 8px 15px',
            fontSize: 12,
            color: 'var(--rv-chart-muted)',
        },
        legendSwatch: {
            display: 'inline-block',
            width: 10,
            height: 10,
            marginRight: 6,
            borderRadius: 3,
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        tooltipWrapper: {
            padding: '0px 10px',
            // minWidth: '300px',
        },
        tooltipTitle: {
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '1rem',
            paddingBottom: '10px',
        },
        tooltipList: {
            listStyleType: 'none',
            padding: 0,
            '& span': {
                fontWeight: 600,
            },
        },
        formControl: {
            display: 'flex',
            flexFlow: 'row wrap',
            alignItems: 'center',
            justifyContent: 'center',
        },
        formLabel: {
            marginRight: '10px',
        },
    }),
);

// export enum GroupOptionEnum {
//     LIVE = 'live',
//     DEAD = 'dead',
// }

interface MattersByCountryChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

export const MattersByCountryChart: React.FC<MattersByCountryChartProps> = (props) => {
    const classes = useStyles();

    // const [tooltipContent, setTooltipContent] = useState<string>('');
    // const [groupOption, setGroupOption] = React.useState<GroupOptionEnum>(GroupOptionEnum.LIVE);
    const [isLive, setIsLive] = React.useState<boolean>(props.maximizeSelected ? props.maximizeSelected.isLive :  true);
    const [isDead, setIsDead] = React.useState<boolean>(props.maximizeSelected ? props.maximizeSelected.isDead :  false);

    const [width, setWidth] = React.useState<number>(828);
    const [height, setHeight] = React.useState<number>(324);

    const matterByCountryParams: MatterByCountryParams = {
        entityGuid: props.chartFilter?.entityGuid,
        entityType: props.chartFilter?.entityType,
        responsibility: props.chartFilter?.responsibility,
        showAllRecords: props.showAllRecords,
        filter: props.chartFilter?.filter,
    };

    const matterByCountryQuery = useMatterByCountryQuery(matterByCountryParams);

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();
    const mapRefContainer = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (mapRefContainer.current && !matterByCountryQuery.loading) {
            // tslint:disable-next-line: no-shadowed-variable
            const { width, height } = mapRefContainer.current.getBoundingClientRect();
            setWidth(width);
            setHeight(height);
        }
    // tslint:disable-next-line: align
    }, [matterByCountryQuery.loading]);

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const onCloseChart = () => {
        if (props.onCloseChart) {
            return props.onCloseChart(`isMattersByCountryChart`);
        }
    };

    const onMaximize = () => {
        if (props.onMaximizeSelected) {
            props.onMaximizeSelected({
                isLive: isLive,
                isDead: isDead,
            });
        }
    };

    // tslint:disable-next-line: no-any
    const onDownload =
        (name: string, extension: string = 'jpg') =>
        // tslint:disable-next-line: no-any
        (image: any) => {
            const a = document.createElement('a');
            a.href = image;
            a.download = createFileName(extension, name);
            a.click();
        };

    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload('Top Referrers by period chart'));
            // tslint:disable-next-line: align
        }, 500);
    };

    const chartControllerComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <ChartInfo
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        className={`${classes.helpIcon} rv-chart-help`}
                    />
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximizeSelected && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon className={classes.closeIcon} fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    const handleTotalClick = (isCurrent: boolean) => {
        const searchCriterias = new Array<SearchCriteria>();

        if (props.getSelectedItem) {
            searchCriterias.push({ property: 'CountryTotal', operator: 'eq', comparison: 'Total' });
            searchCriterias.push({ property: 'Status', operator: 'eq', comparison: 'NOTNULL' });
            props.getSelectedItem(
                searchCriterias,
                false,
                // tslint:disable-next-line: max-line-length
                `Matters By Country`,
                DrillDownType.matterSummaryDrilldown,
                'Filing Details',
            );
        }
    };

    // const geoUrl = 'https://raw.githubusercontent.com/deldersveld/topojson/master/world-countries.json';
    const geoUrl = `${cdnUrl}/assets/json/world-countries.json`;

    const LightTooltip = withStyles((theme: Theme) => ({
        tooltip: {
            backgroundColor: theme.palette.common.white,
            color: 'rgba(0, 0, 0, 0.87)',
            border: '1px solid #e8ebf3',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(16, 26, 51, 0.14)',
            fontSize: 11,
        },
    }))(Tooltip);

    const onClickCountry = (parmas: {
        name: string, 
        matterCountry: MatterCountry | undefined,
        europianUnionData: MatterCountry | undefined,
        isEU?: boolean
    }) => () => {

        const { name, matterCountry, isEU, europianUnionData } = parmas;

        if (((!isEU && matterCountry) || (isEU && (europianUnionData || matterCountry))) && props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: 'Status', operator: 'eq', comparison: 'NOTNULL' });
            searchCriterias.push({ property: 'Country', operator: isEU ? 'eu' : 'eq', comparison: name });

            props.getSelectedItem(
                searchCriterias,
                false,
                // tslint:disable-next-line: max-line-length
                `Matters By Country`,
                DrillDownType.matterSummaryDrilldown,
                'Filing Details',
            );
        }
    };

    if (matterByCountryQuery.loading && matterByCountryQuery.data === undefined) {
        return (
            <div className={`${classes.loadingWrapper} ${RV_CHART_CLASS}`}>
                {chartControllerComponent()}
                <ChartSkeleton variant="card" />
            </div>
        );
    }

    const geoTooltipTitle = (params: {
        title: string, 
        matterCountry: MatterCountry | undefined, 
        europianUnionData: MatterCountry | undefined,
        isEU?: boolean
    }) => {
        
        const { title, matterCountry, europianUnionData, isEU } = params;

        return (
            <div className={classes.tooltipWrapper}>
                <div className={classes.tooltipTitle}>{`${title} (${matterCountry?.countryTotalCount ?? 0})`}</div>
                {matterCountry && (
                    <ul className={classes.tooltipList}>
                        {matterCountry.countryStatus.map((countryStatus) => (
                            <li>
                                {countryStatus.description}: <span>{formatNumber(countryStatus.count)}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {isEU && europianUnionData ? (
                    <>
                        <div className={classes.tooltipTitle}>{`European Union (${europianUnionData?.countryTotalCount ?? 0})`}</div>
                        {europianUnionData && (
                            <ul className={classes.tooltipList}>
                                {europianUnionData.countryStatus.map((countryStatus) => (
                                    <li>
                                        {countryStatus.description}: <span>{formatNumber(countryStatus.count)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                ) : null}
            </div>
        );
    };

    const onCheckboxChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        if (name === 'live') {
            setIsLive(checked);
            if (!checked && !isDead) {
                setIsDead(true);
            }
        } else {
            setIsDead(checked);
            if (!checked && !isLive) {
                setIsLive(true);
            }
        }
    };

    const { deadMattersCount, totalMattersCount, liveMattersCount, liveMatterCountries, deadMatterCountries } = matterByCountryQuery.data!.statistics.matter.mattersByCountry;

    const scale = (Math.min(width / 2, height) / Math.PI) + 22;
    
    const subTitle = [
        isLive ? `${formatNumber(liveMattersCount)} live` : null,
        isDead ? `${formatNumber(deadMattersCount)} dead` : null
    ].filter(n => n).join(', ');


    const euCountry = [
        'Austria', 
        'Belgium', 
        'Bulgaria', 
        'Croatia',
        'Republic of Cyprus', 
        'Czech Republic', 
        'Denmark', 
        'Estonia', 
        'Finland', 
        'France', 
        'Germany', 
        'Greece', 
        'Hungary', 
        'Ireland', 
        'Italy', 
        'Latvia', 
        'Lithuania', 
        'Luxembourg', 
        'Malta', 
        'Netherlands', 
        'Poland', 
        'Portugal', 
        'Romania', 
        'Slovakia', 
        'Slovenia', 
        'Spain',
        'Sweden'
    ];

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}`} ref={entireChartRef}>
            {chartControllerComponent()}
            <div className={classes.titleContainer}>
                <ChartHeading icon={<PublicOutlinedIcon />} title="Matters" />
                <div className={`${classes.total} h2`} onClick={() => handleTotalClick(true)}>
                    <AnimatedNumber value={totalMattersCount} format={(amount) => `${formatNumber(amount)}`} />
                </div>
                <div className="h3" onClick={() => handleTotalClick(false)}>
                    {subTitle}
                </div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }} ref={mapRefContainer}>
                <ComposableMap
                    projectionConfig={{ scale: scale }}
                    width={width}
                    height={height}
                    // projection="geo"
                >
                    <ZoomableGroup>
                        <Geographies geography={geoUrl}>
                            {({ geographies }) =>
                                geographies.map((geo) => {

                                    // tslint:disable-next-line: no-any
                                    let country: MatterCountry | undefined = undefined;

                                    const stateName: string = geo.properties.name.toLowerCase();

                                    let europianUnionData: MatterCountry | undefined = undefined;

                                    if (isLive && isDead) {

                                        const data: MatterCountry[] = liveMatterCountries.concat(deadMatterCountries ?? []) || [];

                                        country = data
                                            ? data.find((c) => {
                                                if (c.country.toLowerCase() === 'republic of korea' && stateName.toLowerCase() === 'south korea') {
                                                    return true;
                                                }
                                                return c.country.toLowerCase() === stateName
                                            })
                                            : undefined;

                                        europianUnionData = data.find((c) => c.country.toLowerCase() === 'european union');
                                        
                                            
                                    } else if (isLive && !isDead) {
                                        country = liveMatterCountries ? liveMatterCountries.find((c) => {
                                            if (c.country.toLowerCase() === 'republic of korea' && stateName.toLowerCase() === 'south korea') {
                                                return true;
                                            }
                                            return c.country.toLowerCase() === stateName;
                                        }) : undefined;

                                        europianUnionData = liveMatterCountries ? liveMatterCountries.find((c) => c.country.toLowerCase() === 'european union') : undefined;

                                    } else if (isDead && !isLive) {
                                        country = deadMatterCountries ? deadMatterCountries.find((c) => {
                                            if (c.country.toLowerCase() === 'republic of korea' && stateName.toLowerCase() === 'south korea') {
                                                return true;
                                            }
                                            return c.country.toLowerCase() === stateName;
                                        }) : undefined;

                                        europianUnionData = deadMatterCountries ? deadMatterCountries.find((c) => c.country.toLowerCase() === 'european union') : undefined;
                                    }

                                    const isEU = euCountry.some(
                                        eu => eu.toLowerCase() === geo.properties.name.toLowerCase()
                                    );
                                    
                                    // EU countries always get gold, others get blue if active, else gray
                                    const fillColor = (isEU && europianUnionData?.countryTotalCount) || country
                                            ? RV_CHART_COLORS.primary // Blue for active non-EU
                                            : '#dfe5f1'; // Gray for inactive non-EU

                                    return (
                                        <LightTooltip key={geo.rsmKey} title={geoTooltipTitle({
                                            title: geo.properties.name, 
                                            matterCountry: country, 
                                            europianUnionData,
                                            isEU
                                        })}>
                                            <Geography
                                                stroke="#FFF"
                                                strokeWidth={0.5}
                                                key={geo.rsmKey}
                                                geography={geo}
                                                onClick={onClickCountry({
                                                    name: geo.properties.name,
                                                    matterCountry: country,
                                                    europianUnionData,
                                                    isEU
                                                })}
                                                style={{
                                                    default: {
                                                        fill: fillColor,
                                                        outline: 'none',
                                                    },
                                                    hover: {
                                                        fill: RV_CHART_COLORS.primary,
                                                        outline: 'none',
                                                    },
                                                    pressed: {
                                                        fill: '#0f4c97',
                                                        outline: 'none',
                                                    },
                                                }}
                                            />
                                        </LightTooltip>
                                    );
                            })}
                        </Geographies>
                    </ZoomableGroup>
                </ComposableMap>
            </div>
            <div className={classes.legend}>
                <span>
                    <i className={classes.legendSwatch} style={{ backgroundColor: RV_CHART_COLORS.primary }} />
                    Countries with matters
                </span>
                <span>
                    <i className={classes.legendSwatch} style={{ backgroundColor: '#dfe5f1' }} />
                    No matters
                </span>
            </div>
            <FormControl component="div" className={classes.formControl}>
                <FormLabel component="legend" className={classes.formLabel}>
                    Include
                </FormLabel>
                <FormGroup className={classes.formGroup}>
                    <FormControlLabel control={<Checkbox checked={isLive} color="primary" name="live" onChange={onCheckboxChange('live')} />} label="Live" />
                    <FormControlLabel control={<Checkbox checked={isDead} color="primary" name="dead" onChange={onCheckboxChange('dead')} />} label="Dead" />
                </FormGroup>
            </FormControl>
        </div>
    );
};
